import { useMemo, useState } from 'react';
import { useReactTable, getCoreRowModel, ColumnDef, flexRender } from '@tanstack/react-table';
import { trpc } from '@/lib/trpc';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { CustomButton } from '@/components/uiWrappers/button';
import { GoUpload } from 'react-icons/go';
import { AnalyticsCard } from './AnalyticsCard';

type ArticleData = {
  articleId: string;
  articleName: string;
  moduleNumber?: number;
  assignmentType: 'general' | 'bucket-one' | 'bucket-two';
  totalViews: number;
  uniqueViews: number;
  likes: number;
  dislikes: number;
};

function getCommonPinningStyles(column: any, isPinned: boolean): React.CSSProperties {
  return {
    position: isPinned ? 'sticky' : 'relative',
    left: isPinned ? 0 : undefined,
    width: column.getSize(),
    zIndex: isPinned ? 10 : 1,
    boxShadow: isPinned ? '2px 0 5px -2px rgba(0,0,0,0.1)' : undefined,
  };
}

export function ArticleAnalytics() {
  const [isExporting, setIsExporting] = useState(false);
  const { data, isLoading } = trpc.admin.analytics.getArticleAnalytics.useQuery();
  const trpcUtils = trpc.useUtils();

  const handleExport = async () => {
    setIsExporting(true);
    try {
      const csvData = await trpcUtils.admin.analytics.exportArticleAnalyticsCSV.fetch();

      if (csvData) {
        // Create timestamp for filename
        const now = new Date();
        const timestamp = now.toISOString().replace(/[:.]/g, '-').slice(0, -5);

        const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `article_analytics_${timestamp}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (error) {
      console.error('Export failed:', error);
    } finally {
      setIsExporting(false);
    }
  };

  const articles = useMemo(() => data?.articles || [], [data?.articles]);

  const columns = useMemo<ColumnDef<ArticleData>[]>(
    () => [
      {
        id: 'articleName',
        accessorKey: 'articleName',
        header: 'Article Name',
        size: 300,
        cell: (info) => <div className="font-medium">{info.getValue() as string}</div>,
      },
      {
        id: 'module',
        accessorKey: 'moduleNumber',
        header: 'Module',
        cell: (info) => info.getValue() || '-',
      },
      {
        id: 'type',
        accessorKey: 'assignmentType',
        header: 'Type',
        cell: (info) => {
          const value = info.getValue() as string;
          return (
            <span className="capitalize text-sm">
              {value.replace('-', ' ')}
            </span>
          );
        },
      },
      {
        id: 'totalViews',
        accessorKey: 'totalViews',
        header: 'Total Views',
        cell: (info) => <span className="text-brand">{info.getValue() as number}</span>,
      },
      {
        id: 'uniqueViews',
        accessorKey: 'uniqueViews',
        header: 'Unique Views',
        cell: (info) => <span className="text-brand">{info.getValue() as number}</span>,
      },
      {
        id: 'likes',
        accessorKey: 'likes',
        header: 'Likes',
        cell: (info) => <span className="text-green-600">{info.getValue() as number}</span>,
      },
      {
        id: 'dislikes',
        accessorKey: 'dislikes',
        header: 'Dislikes',
        cell: (info) => <span className="text-red-600">{info.getValue() as number}</span>,
      },
    ],
    []
  );

  const table = useReactTable({
    data: articles,
    columns,
    getCoreRowModel: getCoreRowModel(),
    state: {
      columnPinning: {
        left: ['articleName'],
      },
    },
    enableColumnPinning: true,
  });

  // Calculate summary stats
  const totalArticles = articles.length;
  const totalViews = articles.reduce((sum, a) => sum + a.totalViews, 0);
  const uniqueViews = articles.reduce((sum, a) => sum + a.uniqueViews, 0);
  const totalLikes = articles.reduce((sum, a) => sum + a.likes, 0);
  const totalDislikes = articles.reduce((sum, a) => sum + a.dislikes, 0);
  const avgViewsPerArticle = totalArticles > 0 ? (totalViews / totalArticles).toFixed(1) : '0';

  // Calculate re-read rate: articles with totalViews > uniqueViews (meaning users came back)
  const articlesWithRereads = articles.filter(a => a.totalViews > a.uniqueViews).length;
  const rereadRate = totalArticles > 0 ? ((articlesWithRereads / totalArticles) * 100).toFixed(1) : '0';

  return (
    <div className="flex-1 min-w-0 space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <AnalyticsCard
          label="Total Articles"
          value={totalArticles}
          tooltip="Total number of articles available in the system"
        />
        <AnalyticsCard
          label="Total Views"
          value={totalViews}
          tooltip="Sum of all article reads across all users"
        />
        <AnalyticsCard
          label="Unique Readers"
          value={uniqueViews}
          tooltip="Number of unique users who have read at least one article"
        />
        <AnalyticsCard
          label="Avg Views per Article"
          value={avgViewsPerArticle}
          tooltip="Average number of reads per article"
        />
        <AnalyticsCard
          label="Total Reactions"
          value={
            <>
              <span className="text-green-600">{totalLikes}</span> / <span className="text-red-600">{totalDislikes}</span>
            </>
          }
          tooltip="Total number of likes and dislikes across all articles"
        />
        <AnalyticsCard
          label="Re-read Rate"
          value={`${rereadRate}%`}
          tooltip="Percentage of articles that users returned to read more than once"
        />
      </div>

      <div className="bg-white min-w-0">
        {/* Section Header */}
        <div className="pr-4 pb-4 border-b border-border flex justify-between items-center">
          <h2 className="text-xl font-bold text-foreground">Article Performance</h2>

          <CustomButton
            type="button"
            onClick={handleExport}
            disabled={isExporting}
            variant="outline"
            size="lg"
            className="rounded-lg flex items-center gap-2"
          >
            <GoUpload className="size-5 text-primary" data-icon="inline-start" />
            Export
          </CustomButton>
        </div>

        <div className="flex flex-col justify-between">
          <div className="overflow-x-auto max-w-full">
            {isLoading ? (
              <div className="p-12 text-center">
                <p className="text-gray-500">Loading analytics...</p>
              </div>
            ) : articles.length === 0 ? (
              <div className="p-12 text-center">
                <p className="text-gray-500">No article data available</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  {table.getHeaderGroups().map((headerGroup) => (
                    <TableRow key={headerGroup.id}>
                      {headerGroup.headers.map((header) => {
                        const isPinned = header.column.getIsPinned() === 'left';
                        return (
                          <TableHead
                            key={header.id}
                            className="bg-gray-50 text-xs font-medium text-gray-500 uppercase tracking-wider"
                            style={getCommonPinningStyles(header.column, isPinned)}
                          >
                            {header.isPlaceholder
                              ? null
                              : flexRender(header.column.columnDef.header, header.getContext())}
                          </TableHead>
                        );
                      })}
                    </TableRow>
                  ))}
                </TableHeader>
                <TableBody>
                  {table.getRowModel().rows.map((row) => (
                    <TableRow key={row.id} className="group hover:bg-gray-50">
                      {row.getVisibleCells().map((cell) => {
                        const isPinned = cell.column.getIsPinned() === 'left';
                        const isNameColumn = cell.column.id === 'articleName';
                        return (
                          <TableCell
                            key={cell.id}
                            className={`whitespace-nowrap bg-white ${
                              isNameColumn
                                ? 'text-sm font-semibold text-gray-900'
                                : 'text-sm font-medium text-gray-700'
                            } group-hover:bg-gray-50`}
                            style={getCommonPinningStyles(cell.column, isPinned)}
                          >
                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
